import React, { useState } from 'react'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useQueryClient } from '@tanstack/react-query'
import { Formik } from 'formik'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import EMobilityWallboxChargeTariffsFieldArray from './tariff-editor-input-fields/EMobilityWallboxChargeTariffsFieldArray'
import EMobilityWallboxDischargeTariffsFieldArray from './tariff-editor-input-fields/EMobilityWallboxDischargeTariffsFieldArray'
import { addWallboxPrices } from '../api/add-wallbox-prices.api'
import { useEmobilityWallboxTariffsFormInitializer } from 'src/Elements/P3_eMobility/emobility-wallbox/hooks'

export interface EMobilityWallboxTariffsEditorProps {
  open: boolean
  onClose: () => void
  place: number
  chargePrices: { validFrom: number | null; price: number }[]
  dischargePrices: { validFrom: number | null; price: number }[]
  storageId: string
  wallboxId: string
  wallboxName: string
  wallboxIndex: number
  isEdisonConnect: boolean
}

export interface WallboxTariffsFormData {
  chargePrices: Array<{ validFrom: Date; price: number }>
  dischargePrices: Array<{ validFrom: Date; price: number }>
}

const EMobilityWallboxTariffsEditor: React.FC<EMobilityWallboxTariffsEditorProps> = ({
  open,
  place,
  onClose,
  chargePrices,
  dischargePrices,
  storageId,
  wallboxId,
  wallboxName,
  wallboxIndex,
  isEdisonConnect,
}): JSX.Element => {
  const [isChargePriceLoading, setIsChargePriceLoading] = useState<boolean>(false)
  const [isDischargePriceLoading, setIsDischargePriceLoading] = useState<boolean>(false)

  const queryClient = useQueryClient()

  const initialChargePricesFormData = chargePrices.map(item => {
    return {
      validFrom: item.validFrom ? new Date(item.validFrom) : new Date(),
      price: item.price,
    }
  })
  const initialDischargePricesFormData = dischargePrices.map(item => {
    return {
      validFrom: item.validFrom ? new Date(item.validFrom) : new Date(),
      price: item.price,
    }
  })

  const { initialValues, validationSchema } = useEmobilityWallboxTariffsFormInitializer({
    chargePrices: initialChargePricesFormData,
    dischargePrices: initialDischargePricesFormData,
  })

  const authFetch = useAuthFetchWithResult()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const { t } = useCustomTranslation()

  const handleSubmit = async (formData: WallboxTariffsFormData): Promise<void> => {
    const chargePricePayload = formData.chargePrices.map(item => {
      return {
        from: Date.parse(item.validFrom.toString()),
        price: Number(item.price),
      }
    })

    const dischargePricePayload = formData.dischargePrices.map(item => {
      return {
        from: Date.parse(item.validFrom.toString()),
        price: Number(item.price),
      }
    })
    addWallboxPrices({
      payload: chargePricePayload,
      setIsLoading: setIsChargePriceLoading,
      onClose,
      priceType: 'charge',
      serialNo: storageId,
      wallboxIndex,
      authFetch,
      enqueueErrorStack,
      enqueueSuccessStack,
      queryClient,
      t,
    })

    isEdisonConnect &&
      addWallboxPrices({
        payload: dischargePricePayload,
        setIsLoading: setIsDischargePriceLoading,
        onClose,
        priceType: 'discharge',
        serialNo: storageId,
        wallboxIndex,
        authFetch,
        enqueueErrorStack,
        enqueueSuccessStack,
        queryClient,
        t,
      })
  }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
      {({ handleSubmit, isValid, dirty }) => {
        return (
          <Dialog
            title={t('emobility.wallbox.editTariffs.title', { wallbox: wallboxName, id: wallboxId })}
            confirmClose={dirty}
            onClose={onClose}
            place={place}
            actions={{
              primary: {
                isLoading: isChargePriceLoading || isDischargePriceLoading,
                disabled: !dirty || !isValid,
                name: t('emobility.wallbox.editTariffs.save'),
                onClick: handleSubmit,
              },
              secondary: {
                name: t('emobility.wallbox.editTariffs.cancel'),
              },
            }}
          >
            <EMobilityWallboxChargeTariffsFieldArray />
            {isEdisonConnect && <EMobilityWallboxDischargeTariffsFieldArray />}
          </Dialog>
        )
      }}
    </Formik>
  )
}

export default EMobilityWallboxTariffsEditor
